import { useEffect, useState } from "react";
import { Autocomplete, TextField } from "@mui/material";
import DSDialog from "views/system-design/DSDialog";
import DSDialogTitle from "views/system-design/DSDialogTitle";
import DSDialogContent from "views/system-design/DSDialogContent";
import DSDialogActions from "views/system-design/DSDialogActions";
import DSButton from "views/system-design/DSButton";
import DSBox from "views/system-design/DSBox";
import DSTypography from "views/system-design/DSTypography";
import DSTextField from "views/system-design/DSTextField";
import DSEnumSelect from "views/system-design/DSEnumSelect";
import DSSegmentedTabs from "views/system-design/DSSegmentedTabs";
import { MonitorType } from "../../../api/admin";
import OffHoursAlertingPicker from "./OffHoursAlertingPicker";
import { useAlertPolicies } from "../../AlertPolicies/hooks/useAlertPolicies";
import {
  useAvailableMonitorNames,
  useCreateMonitor,
} from "../hooks/useKlydoMonitors";

interface CreateMonitorModalProps {
  open: boolean;
  onClose: () => void;
}

const CreateMonitorModal = ({ open, onClose }: CreateMonitorModalProps) => {
  const [type, setType] = useState<MonitorType>(MonitorType.Cron);
  const [name, setName] = useState("");
  const [intervalHours, setIntervalHours] = useState(24);
  const [maxErrors, setMaxErrors] = useState(1);
  const [fromVersion, setFromVersion] = useState("");
  const [alertPolicyId, setAlertPolicyId] = useState("");
  const [alertOutsideWorkHours, setAlertOutsideWorkHours] = useState(false);

  const { data: availableNames } = useAvailableMonitorNames();
  const { data: alertPolicies } = useAlertPolicies();
  const { mutate: create, isPending: isCreating } = useCreateMonitor();

  const isClockInternal = type === MonitorType.ClockInternal;

  useEffect(() => {
    if (open) {
      setType(MonitorType.Cron);
      setName("");
      setIntervalHours(24);
      setMaxErrors(1);
      setFromVersion("");
      setAlertPolicyId("");
      setAlertOutsideWorkHours(false);
    }
  }, [open]);

  const policyOptions = [
    { value: "", label: "— None —" },
    ...(alertPolicies?.map((p) => ({ value: p.id, label: p.name })) ?? []),
  ];

  const trimmedName = name.trim();
  const isUnknownName =
    !isClockInternal &&
    trimmedName.length > 0 &&
    availableNames !== undefined &&
    !availableNames.includes(trimmedName);

  const handleCreate = () => {
    if (isClockInternal) {
      // Clocks report failures by name/id; the interval is irrelevant (the
      // clocks schedule themselves and the server defaults it to 0).
      // fromVersion gates which firmware versions may report.
      create(
        {
          name: trimmedName,
          type: MonitorType.ClockInternal,
          fromVersion: fromVersion === "" ? null : Number(fromVersion),
          alertPolicyId: alertPolicyId || null,
          alertOutsideWorkHours,
        },
        { onSuccess: onClose },
      );
      return;
    }

    create(
      {
        name,
        type: MonitorType.Cron,
        intervalHours,
        maxTemporaryErrorsBeforeAlert: maxErrors,
        alertPolicyId: alertPolicyId || null,
        alertOutsideWorkHours,
      },
      { onSuccess: onClose },
    );
  };

  const canCreate =
    !isCreating &&
    trimmedName.length > 0 &&
    (isClockInternal || intervalHours > 0);

  return (
    <DSDialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DSDialogTitle>New monitor</DSDialogTitle>
      <DSDialogContent>
        <DSBox sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
          <DSBox sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            <DSTypography variant="caption" color="text.secondary">
              Type
            </DSTypography>
            <DSSegmentedTabs
              size="small"
              value={type}
              onChange={(value) =>
                setType((value as MonitorType) ?? MonitorType.Cron)
              }
              options={[
                { label: "Cron", value: MonitorType.Cron },
                { label: "Clock internal", value: MonitorType.ClockInternal },
              ]}
            />
          </DSBox>

          <DSBox sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            <DSTypography variant="caption" color="text.secondary">
              Monitor
            </DSTypography>
            {isClockInternal ? (
              <>
                <DSTextField
                  size="small"
                  value={name}
                  placeholder="clock-boot-loop"
                  onChange={(e) => setName(e.target.value)}
                />
                <DSTypography variant="caption" color="text.secondary">
                  The monitor id clocks reference when reporting a failure. New
                  ids are also created automatically on the first report.
                </DSTypography>
              </>
            ) : (
              <>
                <Autocomplete
                  freeSolo
                  size="small"
                  options={availableNames ?? []}
                  value={name}
                  onInputChange={(_, newValue) => setName(newValue)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="shopify-collection-mapping"
                    />
                  )}
                />
                <DSTypography variant="caption" color="text.secondary">
                  Type the monitor name, or pick from suggestions (registered
                  monitors without an entry yet).
                </DSTypography>
                {isUnknownName && (
                  <DSTypography variant="caption" color="warning.main">
                    "{trimmedName}" isn't in the available list — if it's not a
                    registered monitor it will be created but never run.
                  </DSTypography>
                )}
              </>
            )}
          </DSBox>

          {isClockInternal ? (
            <DSBox sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
              <DSTextField
                label="From version (optional)"
                type="number"
                size="small"
                value={fromVersion}
                onChange={(e) => setFromVersion(e.target.value)}
              />
              <DSTypography variant="caption" color="text.secondary">
                Reports from clocks running a version lower than this are
                ignored. Leave empty to accept all versions.
              </DSTypography>
            </DSBox>
          ) : (
            <>
              <DSTextField
                label="Interval (hours)"
                type="number"
                size="small"
                value={intervalHours}
                onChange={(e) => setIntervalHours(Number(e.target.value))}
              />
              <DSTextField
                label="Max temporary errors before alert"
                type="number"
                size="small"
                value={maxErrors}
                onChange={(e) => setMaxErrors(Number(e.target.value))}
              />
            </>
          )}

          <DSBox sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            <DSTypography variant="caption" color="text.secondary">
              Alert policy
            </DSTypography>
            <DSEnumSelect
              size="small"
              value={alertPolicyId}
              options={policyOptions}
              onChange={setAlertPolicyId}
            />
          </DSBox>

          <OffHoursAlertingPicker
            value={alertOutsideWorkHours}
            onChange={setAlertOutsideWorkHours}
          />
        </DSBox>
      </DSDialogContent>
      <DSDialogActions sx={{ justifyContent: "flex-end", px: 3, pb: 2 }}>
        <DSButton size="small" onClick={onClose}>
          Cancel
        </DSButton>
        <DSButton
          variant="contained"
          size="small"
          disabled={!canCreate}
          onClick={handleCreate}
        >
          Create
        </DSButton>
      </DSDialogActions>
    </DSDialog>
  );
};

export default CreateMonitorModal;
